import { createJointInsightsApiSaga, jointInsightsApiReducer, JointInsightsApiState } from '@om1/joint-insights-api'
import { auth0Reducer, Auth0State } from '@om1/platform-authentication'
import { platformConfigReducer, PlatformConfigState } from '@om1/platform-config/state'
import { createNotificationSaga, notificationActions, notificationReducer, NotificationState } from '@om1/platform-notifications'
import { createTrackingSaga } from '@om1/platform-tracking'
import { configureStore } from '@reduxjs/toolkit'
import { AnalyticsBrowser } from '@segment/analytics-next'
import { connectRouter, RouterState } from 'connected-react-router'
import { LocationState } from 'history'
import { OptionsObject, SnackbarKey, SnackbarMessage } from 'notistack'
import { AnyAction, applyMiddleware, CombinedState, combineReducers, Middleware, Reducer } from 'redux'
import dynamicMiddlewares from 'redux-dynamic-middlewares'
import { SagaMiddleware } from 'redux-saga'
import { all, put, spawn } from 'redux-saga/effects'
import keepAlive from './keep-alive'
import { createSurveySaga } from './sagas'
import { languageReducer } from './shared/providers/internationalization/language-reducer'
import { LanguageState } from './shared/providers/internationalization/language-state'
import { surveyReducer, SurveyState } from './state/survey-reducer'

export const createReducer = (history: any) =>
    combineReducers({
        router: connectRouter(history),
        language: languageReducer,
        notification: notificationReducer,
        survey: surveyReducer,
        // modules:
        platformConfig: platformConfigReducer,
        jointInsightsApi: jointInsightsApiReducer,
        auth0: auth0Reducer
    })
export const appReducer = createReducer(window.history)

export type JointInsightsState = ReturnType<typeof appReducer>

export function createAppStore(
    reducer: Reducer<
        CombinedState<{
            router: RouterState<LocationState>
            language: LanguageState
            notification: NotificationState
            // modules:
            platformConfig: PlatformConfigState
            jointInsightsApi: JointInsightsApiState
            auth0: Auth0State
            survey: SurveyState
        }>,
        AnyAction
    >,
    sagaMiddleware: SagaMiddleware,
    ...additionalMiddlewares: Middleware[]
) {
    const middlewares = [dynamicMiddlewares, ...additionalMiddlewares]
    return configureStore({
        reducer: reducer,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: { ignoredActions: ['@@notifications/success', '@@notifications/error', '@@notifications/warning'] }
            }).concat(sagaMiddleware),
        enhancers: [applyMiddleware(...middlewares)]
    })
}

export type AppStore = ReturnType<typeof createAppStore>

function createOnFail(sagaName: string) {
    function* onFail(error) {
        // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
        const errorMessage = `${sagaName} module has produced too many uncaught errors and is shutting down, these exceptions should be handled.`
        yield console.warn(errorMessage)
        yield console.error(error)
        yield put(notificationActions.error(errorMessage))
    }
    function* onEachError(_next, error, _saga) {
        // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
        const errorMessage = `${sagaName} module has produced an error and is restarting, these exceptions should be handled.`
        yield console.warn(errorMessage)
        yield console.error(error)
        yield put(notificationActions.warning(errorMessage))
    }
    return { onFail, onEachError, disableWarnings: true }
}

export function createRootSaga(enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey, analytics?: AnalyticsBrowser) {
    const notificationSaga = createNotificationSaga(enqueueSnackbar)
    // modules:
    const jointInsightsApiSaga = createJointInsightsApiSaga()
    const surveySaga = createSurveySaga()

    let sagas = [
        { saga: notificationSaga, name: 'Notifications' },
        // modules:
        { saga: jointInsightsApiSaga, name: 'JointInsightsAPI' },
        { saga: surveySaga, name: 'Survey' }
    ]

    if (analytics) {
        const trackingSaga = createTrackingSaga(analytics)
        sagas.push({ saga: trackingSaga, name: 'User Tracking' })
    }

    return function* () {
        yield all(sagas.map((saga) => spawn(keepAlive(saga.saga, createOnFail(saga.name)))))
    }
}

export const JointInsightsPermissions = {
    POST_SURVEY: 'post:joint-insights'
}
